import {
  type SidebarItem,
  type SidebarItemGroup,
  useRequireOrganization,
} from '@startupquickstart/frontend';
import { Activity, Building, Building2, Code, Database, Globe, Home, House, Image, KeyRound, LifeBuoy, Mail, Map, MapPin, Megaphone, MessageSquareText, Palette, Presentation, RefreshCw, Tag, Upload, Video, Wifi, Workflow } from 'lucide-react';
import { useMemo } from 'react';

// Main sidebar configuration
const sidebarGroups: SidebarItemGroup[] = [
  {
    label: 'Content',
    items: [
      {
        title: 'Attractions',
        url: '/attractions',
        icon: MapPin,
      },
      {
        title: 'Tours',
        url: '/tours',
        icon: Map,
      },
    ],
  },
  {
    label: 'Types',
    items: [
      { title: 'Amenity Types', url: '/amenity-types', icon: Wifi },
      { title: 'Attraction Categories', url: '/attraction-categories', icon: Tag },
      { title: 'Attraction Types', url: '/attraction-types', icon: MapPin },
      { title: 'Languages', url: '/languages', icon: Globe },
      { title: 'Theme Types', url: '/theme-types', icon: Palette },
      { title: 'Tour Types', url: '/tour-types', icon: Map },
    ],
  },
  {
    label: 'Locations',
    items: [
      {
        title: 'Greater Areas',
        url: '/greater-areas',
        icon: Building2,
      },
      {
        title: 'Hotels',
        url: '/hotels',
        icon: House,
      },
      {
        title: 'Neighborhoods',
        url: '/neighborhoods',
        icon: Building,
      },
    ],
  },
  {
    label: 'Tools',
    items: [
      {
        title: 'Data Health',
        url: '/data-health',
        icon: Activity,
      },
      {
        title: 'Photo Submissions',
        url: '/photo-submissions',
        icon: Image,
      },
      {
        title: 'Access Requests',
        url: '/access-requests',
        icon: KeyRound,
      },
      {
        title: 'Import',
        url: '/import',
        icon: Upload,
      },
      {
        title: 'API Explorer',
        url: '/api-explorer',
        icon: Code,
      },
      {
        title: 'Redis Cache',
        url: '/settings/redis-cache',
        icon: Database,
      },
      {
        title: 'Clerk Sync',
        url: '/settings/clerk-sync',
        icon: RefreshCw,
      },
      {
        title: 'How-To Video',
        url: '/settings/how-to-video',
        icon: Video,
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        title: 'Processes',
        url: '/processes',
        icon: Workflow,
      },
      {
        title: 'Presentation Shares',
        url: '/presentation-shares',
        icon: Presentation,
      },
      {
        title: 'Release Notes',
        url: '/release-notes',
        icon: Megaphone,
      },
      {
        title: 'Prompts',
        url: '/settings/prompts',
        icon: MessageSquareText,
      },
      {
        title: 'Tickets',
        url: '/internal/support',
        icon: LifeBuoy,
        activeMatch: ['/internal/support'],
      },
    ],
  },
];

/**
 * Custom hook to provide sidebar configuration with dynamic access control
 * Supports JavaScript logic, hooks, and conditional rendering
 */
export function useSidebarConfig(): { baseItems: SidebarItem[]; groups: SidebarItemGroup[] } {
  const { hasOrganization, isInternal } = useRequireOrganization();

  return useMemo(() => {
    // Personal accounts (no organization context) only see Home link
    if (!hasOrganization) {
      return {
        baseItems: [
          {
            title: 'Home',
            url: '/home',
            icon: Home,
          },
        ],
        groups: [],
      };
    }

    // Non-internal organizations see limited navigation
    if (!isInternal) {
      return {
        baseItems: [],
        groups: [
          {
            label: 'Manage',
            items: [
              {
                title: 'Hotels',
                url: '/dashboard',
                icon: House,
                activeMatch: ['/dashboard', '/hotels'],
              },
              {
                title: 'Promotional Offers',
                url: '/promotional-offers',
                icon: Mail,
              },
            ],
          },
        ],
      };
    }

    // Internal organization accounts see full navigation
    // No base items for internal orgs (they see everything in groups)
    const baseItems: SidebarItem[] = [];

    // You can add conditional logic here for groups
    // For example, filter groups based on user permissions:
    const filteredGroups = sidebarGroups.filter(() => {
      // Example: Hide certain groups for non-organization users
      // if (!organization?.id && group.label === 'Admin') return false;
      return true;
    });

    // You can also dynamically modify items within groups
    const processedGroups = filteredGroups.map(group => ({
      ...group,
      items: group.items.filter(() => {
        // Example: Hide certain items based on conditions
        // if (!hasPermission('tours') && item.url === '/tours') return false;
        return true;
      }),
    }));

    return {
      baseItems,
      groups: processedGroups,
    };
  }, [hasOrganization, isInternal]);
}

// Export static configuration for easy modification
export { sidebarGroups };
